import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import { AppHeader } from '../components/AppHeader';
import { AppFooter } from '../components/AppFooter';
import { TrustBar } from '../components/TrustBar';
import { ProcessSteps } from '../components/ProcessSteps';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/Card';
import { Badge } from '../components/Badge';
import { Button } from '../components/Button';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/Accordion';
import { Avatar, AvatarFallback, AvatarImage } from '../components/Avatar';
import { HeartPulse, Stethoscope, Plane, ShieldCheck, Baby, Activity, ArrowRight, CheckCircle2, MapPin, Globe, Search, Shield, ChevronLeft, ChevronRight } from 'lucide-react';
export function Home() {
  const navigate = useNavigate();
  const scrollToContact = () => {
    navigate('/contact');
  };
  return <div className="min-h-screen bg-white flex flex-col" data-id="element-695">
      <Helmet data-id="element-696">
        <title data-id="element-697">Affordable Medical Tourism | World-Class Care Abroad</title>
        <meta name="description" content="Connect with internationally accredited hospitals abroad for top-tier medical, dental, and cosmetic care. Save up to 70% compared to US prices without compromising on quality." data-id="element-698" />
      </Helmet>
      <AppHeader data-id="element-699" />

      <main className="flex-1" data-id="element-700">
        {/* Custom Hero Section matching reference design */}
        <section className="relative min-h-[85vh] flex items-center pt-20 pb-32 overflow-hidden" data-id="element-701">
          {/* Background Image with Gradient Fade */}
          <div className="absolute inset-0 z-0" data-id="element-702">
            <img src="https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?q=80&w=2906&auto=format&fit=crop" alt="Modern hospital facility" className="w-full h-full object-cover object-right" data-id="element-703" />
            <div className="absolute inset-0 bg-white/75" data-id="element-704" />
          </div>

          <div className="container mx-auto px-4 relative z-10" data-id="element-705">
            <div className="max-w-3xl" data-id="element-706">
              {/* Eyebrow */}
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 text-blue-600 mb-8" data-id="element-707">
                <Globe className="w-4 h-4" data-id="element-708" />
                <span className="text-sm font-semibold tracking-wide" data-id="element-709">
                  A $250B+ Global Healthcare Market
                </span>
              </div>

              {/* Headline */}
              <h1 className="text-5xl md:text-7xl font-serif text-slate-900 leading-[1.1] mb-6" data-id="element-710">
                World-Class Care,{' '}
                <span className="text-blue-600 italic" data-id="element-711">Without</span> the
                World-Class Price
              </h1>

              {/* Subheadline */}
              <p className="text-xl text-slate-600 leading-relaxed mb-10 max-w-2xl" data-id="element-712">
                Navigate the global healthcare landscape with evidence-based
                intelligence. Compare JCI-accredited facilities across 20+
                countries and save 40-80% on procedures from cardiac surgery to
                dental implants.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row items-center gap-4 mb-12" data-id="element-713">
                <Button size="lg" className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 h-14 text-base" onClick={() => navigate('/procedures')} data-id="element-714">
                  <Search className="w-5 h-5 mr-2" data-id="element-715" />
                  Search Procedures
                </Button>
                <Button variant="ghost" size="lg" className="w-full sm:w-auto rounded-full px-8 h-14 text-base font-medium text-slate-900 hover:bg-slate-100 hover:text-slate-900" onClick={scrollToContact} data-id="element-716">
                  Get a Free Consultation{' '}
                  <ArrowRight className="w-5 h-5 ml-2" data-id="element-717" />
                </Button>
              </div>

              {/* Trust Indicators */}
              <div className="flex flex-wrap items-center gap-x-8 gap-y-4 text-slate-500" data-id="element-718">
                <div className="flex items-center gap-2" data-id="element-719">
                  <Shield className="w-5 h-5 text-blue-600" data-id="element-720" />
                  <span className="text-sm font-medium" data-id="element-721">
                    JCI-Accredited Facilities
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-722">
                  <ShieldCheck className="w-5 h-5 text-blue-600" data-id="element-723" />
                  <span className="text-sm font-medium" data-id="element-724">
                    Secure and Confidential
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-725">
                  <Globe className="w-5 h-5 text-blue-600" data-id="element-726" />
                  <span className="text-sm font-medium" data-id="element-727">
                    20+ Countries Covered
                  </span>
                </div>
              </div>
            </div>
          </div>

          {/* Floating Card (Bottom Right) */}
          <div className="hidden lg:block absolute bottom-12 right-12 z-20" data-id="element-728">
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-xl border border-white/20 flex items-center gap-4" data-id="element-729">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center text-blue-600" data-id="element-730">
                <Globe className="w-5 h-5" data-id="element-731" />
              </div>
              <div data-id="element-732">
                <p className="font-bold text-slate-900" data-id="element-733">Bangkok, Thailand</p>
                <p className="text-sm text-slate-500" data-id="element-734">
                  60+ JCI-Accredited Hospitals
                </p>
              </div>
            </div>
          </div>

          {/* Carousel Controls (Bottom Center) */}
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex items-center gap-4" data-id="element-735">
            <button className="w-8 h-8 rounded-full bg-white shadow flex items-center justify-center text-slate-600 hover:text-slate-900 transition-colors" data-id="element-736">
              <ChevronLeft className="w-4 h-4" data-id="element-737" />
            </button>
            <div className="flex gap-2" data-id="element-738">
              <div className="w-6 h-2 rounded-full bg-blue-600" data-id="element-739"></div>
              <div className="w-2 h-2 rounded-full bg-slate-300" data-id="element-740"></div>
              <div className="w-2 h-2 rounded-full bg-slate-300" data-id="element-741"></div>
              <div className="w-2 h-2 rounded-full bg-slate-300" data-id="element-742"></div>
            </div>
            <button className="w-8 h-8 rounded-full bg-white shadow flex items-center justify-center text-slate-600 hover:text-slate-900 transition-colors" data-id="element-743">
              <ChevronRight className="w-4 h-4" data-id="element-744" />
            </button>
          </div>
        </section>

        {/* Trust Bar */}
        <div className="bg-slate-50 border-y border-slate-100" data-id="element-745">
          <TrustBar heading="Why thousands choose us for their medical journey" showDisclaimer={false} items={[{
          title: 'Internationally Accredited Facilities',
          description: 'We coordinate care with hospitals meeting stringent global standards.',
          icon: ShieldCheck
        }, {
          title: 'Transparent Pricing',
          description: 'No hidden fees. Know your exact costs before you travel.',
          icon: Activity
        }, {
          title: 'Dedicated Care Team',
          description: '24/7 support from your first inquiry to your safe return home.',
          icon: HeartPulse
        }, {
          title: 'Complete Travel Support',
          description: 'Assistance with flights, visas, and comfortable accommodations.',
          icon: Plane
        }]} data-id="element-746" />
        </div>

        {/* Procedures Grid */}
        <section id="procedures" className="py-20 px-6 md:px-12 max-w-7xl mx-auto" data-id="element-747">
          <div className="text-center mb-12" data-id="element-748">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-749">
              Featured Procedures
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-750">
              World-class treatments at a fraction of the cost. Explore our most
              popular medical and cosmetic procedures.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-751">
            {/* Dental */}
            <Card className="flex flex-col h-full" data-id="element-752">
              <CardHeader data-id="element-753">
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4 text-blue-600" data-id="element-754">
                  <Activity className="w-6 h-6" data-id="element-755" />
                </div>
                <CardTitle data-id="element-756">Dental Care</CardTitle>
                <CardDescription data-id="element-757">
                  Implants, veneers, crowns, and full mouth restorations.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1" data-id="element-758">
                <div className="space-y-4" data-id="element-759">
                  <div data-id="element-760">
                    <span className="text-2xl font-bold text-slate-900" data-id="element-761">
                      from $800
                    </span>
                    <span className="text-sm text-slate-500 ml-2" data-id="element-762">
                      per implant
                    </span>
                  </div>
                  <Badge variant="secondary" className="bg-green-100 text-green-800 hover:bg-green-100" data-id="element-763">
                    Save up to 70% vs US
                  </Badge>
                  <div className="flex flex-wrap gap-2 pt-2" data-id="element-764">
                    <Badge variant="outline" className="text-slate-600" data-id="element-765">
                      <MapPin className="w-3 h-3 mr-1" data-id="element-766" /> Mexico
                    </Badge>
                    <Badge variant="outline" className="text-slate-600" data-id="element-767">
                      <MapPin className="w-3 h-3 mr-1" data-id="element-768" /> Costa Rica
                    </Badge>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex gap-2" data-id="element-769">
                <Button variant="outline" className="flex-1" onClick={() => navigate('/procedures/dental')} data-id="element-770">
                  Details
                </Button>
                <Button className="flex-1" onClick={scrollToContact} data-id="element-771">
                  Get a quote
                </Button>
              </CardFooter>
            </Card>

            {/* Cosmetic */}
            <Card className="flex flex-col h-full" data-id="element-772">
              <CardHeader data-id="element-773">
                <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center mb-4 text-pink-600" data-id="element-774">
                  <HeartPulse className="w-6 h-6" data-id="element-775" />
                </div>
                <CardTitle data-id="element-776">Cosmetic Surgery</CardTitle>
                <CardDescription data-id="element-777">
                  Rhinoplasty, breast augmentation, facelifts, and body
                  contouring.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1" data-id="element-778">
                <div className="space-y-4" data-id="element-779">
                  <div data-id="element-780">
                    <span className="text-2xl font-bold text-slate-900" data-id="element-781">
                      from $3,500
                    </span>
                    <span className="text-sm text-slate-500 ml-2" data-id="element-782">
                      avg. procedure
                    </span>
                  </div>
                  <Badge variant="secondary" className="bg-green-100 text-green-800 hover:bg-green-100" data-id="element-783">
                    Save up to 60% vs US
                  </Badge>
                  <div className="flex flex-wrap gap-2 pt-2" data-id="element-784">
                    <Badge variant="outline" className="text-slate-600" data-id="element-785">
                      <MapPin className="w-3 h-3 mr-1" data-id="element-786" /> Turkey
                    </Badge>
                    <Badge variant="outline" className="text-slate-600" data-id="element-787">
                      <MapPin className="w-3 h-3 mr-1" data-id="element-788" /> Thailand
                    </Badge>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex gap-2" data-id="element-789">
                <Button variant="outline" className="flex-1" onClick={() => navigate('/procedures/cosmetic')} data-id="element-790">
                  Details
                </Button>
                <Button className="flex-1" onClick={scrollToContact} data-id="element-791">
                  Get a quote
                </Button>
              </CardFooter>
            </Card>

            {/* Orthopedic */}
            <Card className="flex flex-col h-full" data-id="element-792">
              <CardHeader data-id="element-793">
                <div className="w-12 h-12 bg-orange-100 rounded-lg flex items-center justify-center mb-4 text-orange-600" data-id="element-794">
                  <Stethoscope className="w-6 h-6" data-id="element-795" />
                </div>
                <CardTitle data-id="element-796">Orthopedic Surgery</CardTitle>
                <CardDescription data-id="element-797">
                  Knee replacement, hip replacement, and spinal surgeries.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1" data-id="element-798">
                <div className="space-y-4" data-id="element-799">
                  <div data-id="element-800">
                    <span className="text-2xl font-bold text-slate-900" data-id="element-801">
                      from $8,500
                    </span>
                    <span className="text-sm text-slate-500 ml-2" data-id="element-802">
                      joint replacement
                    </span>
                  </div>
                  <Badge variant="secondary" className="bg-green-100 text-green-800 hover:bg-green-100" data-id="element-803">
                    Save up to 75% vs US
                  </Badge>
                  <div className="flex flex-wrap gap-2 pt-2" data-id="element-804">
                    <Badge variant="outline" className="text-slate-600" data-id="element-805">
                      <MapPin className="w-3 h-3 mr-1" data-id="element-806" /> India
                    </Badge>
                    <Badge variant="outline" className="text-slate-600" data-id="element-807">
                      <MapPin className="w-3 h-3 mr-1" data-id="element-808" /> Mexico
                    </Badge>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex gap-2" data-id="element-809">
                <Button variant="outline" className="flex-1" onClick={() => navigate('/procedures/orthopedic')} data-id="element-810">
                  Details
                </Button>
                <Button className="flex-1" onClick={scrollToContact} data-id="element-811">
                  Get a quote
                </Button>
              </CardFooter>
            </Card>

            {/* Cardiac */}
            <Card className="flex flex-col h-full" data-id="element-812">
              <CardHeader data-id="element-813">
                <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center mb-4 text-red-600" data-id="element-814">
                  <HeartPulse className="w-6 h-6" data-id="element-815" />
                </div>
                <CardTitle data-id="element-816">Cardiac Care</CardTitle>
                <CardDescription data-id="element-817">
                  Bypass surgery, valve replacement, and angioplasty.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1" data-id="element-818">
                <div className="space-y-4" data-id="element-819">
                  <div data-id="element-820">
                    <span className="text-2xl font-bold text-slate-900" data-id="element-821">
                      from $12,000
                    </span>
                    <span className="text-sm text-slate-500 ml-2" data-id="element-822">
                      bypass surgery
                    </span>
                  </div>
                  <Badge variant="secondary" className="bg-green-100 text-green-800 hover:bg-green-100" data-id="element-823">
                    Save up to 80% vs US
                  </Badge>
                  <div className="flex flex-wrap gap-2 pt-2" data-id="element-824">
                    <Badge variant="outline" className="text-slate-600" data-id="element-825">
                      <MapPin className="w-3 h-3 mr-1" data-id="element-826" /> India
                    </Badge>
                    <Badge variant="outline" className="text-slate-600" data-id="element-827">
                      <MapPin className="w-3 h-3 mr-1" data-id="element-828" /> Thailand
                    </Badge>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex gap-2" data-id="element-829">
                <Button variant="outline" className="flex-1" onClick={() => navigate('/procedures/cardiac')} data-id="element-830">
                  Details
                </Button>
                <Button className="flex-1" onClick={scrollToContact} data-id="element-831">
                  Get a quote
                </Button>
              </CardFooter>
            </Card>

            {/* Fertility */}
            <Card className="flex flex-col h-full" data-id="element-832">
              <CardHeader data-id="element-833">
                <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4 text-purple-600" data-id="element-834">
                  <Baby className="w-6 h-6" data-id="element-835" />
                </div>
                <CardTitle data-id="element-836">Fertility (IVF)</CardTitle>
                <CardDescription data-id="element-837">
                  IVF cycles, egg freezing, and comprehensive fertility
                  treatments.
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1" data-id="element-838">
                <div className="space-y-4" data-id="element-839">
                  <div data-id="element-840">
                    <span className="text-2xl font-bold text-slate-900" data-id="element-841">
                      from $4,500
                    </span>
                    <span className="text-sm text-slate-500 ml-2" data-id="element-842">
                      per IVF cycle
                    </span>
                  </div>
                  <Badge variant="secondary" className="bg-green-100 text-green-800 hover:bg-green-100" data-id="element-843">
                    Save up to 65% vs US
                  </Badge>
                  <div className="flex flex-wrap gap-2 pt-2" data-id="element-844">
                    <Badge variant="outline" className="text-slate-600" data-id="element-845">
                      <MapPin className="w-3 h-3 mr-1" data-id="element-846" /> Spain
                    </Badge>
                    <Badge variant="outline" className="text-slate-600" data-id="element-847">
                      <MapPin className="w-3 h-3 mr-1" data-id="element-848" /> Mexico
                    </Badge>
                  </div>
                </div>
              </CardContent>
              <CardFooter className="flex gap-2" data-id="element-849">
                <Button variant="outline" className="flex-1" onClick={() => navigate('/procedures/fertility')} data-id="element-850">
                  Details
                </Button>
                <Button className="flex-1" onClick={scrollToContact} data-id="element-851">
                  Get a quote
                </Button>
              </CardFooter>
            </Card>
          </div>
        </section>

        {/* How it works */}
        <section id="how-it-works" className="bg-slate-50 py-20" data-id="element-852">
          <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-853">
            <ProcessSteps heading="Your journey to better health" intro="We've simplified the process of seeking medical care abroad. From your first inquiry to your safe return home, we're with you every step of the way." steps={[{
            title: '1. Tell us your needs',
            description: 'Fill out our secure form with your medical needs and preferences. Our care team will review your case confidentially.'
          }, {
            title: '2. Receive matched quotes',
            description: 'We consult with top accredited hospitals to provide you with personalized treatment plans and transparent pricing.'
          }, {
            title: '3. Plan your travel & treatment',
            description: 'Once you choose a facility, we assist with scheduling, flights, visas, and comfortable accommodations near the hospital.'
          }, {
            title: '4. Get treated & recover',
            description: 'Travel with peace of mind. Receive world-class care and recover in a relaxing environment with our local support team.'
          }]} data-id="element-854" />
          </div>
        </section>

        {/* Featured Destinations */}
        <section id="destinations" className="py-20 px-6 md:px-12 max-w-7xl mx-auto" data-id="element-855">
          <div className="text-center mb-12" data-id="element-856">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-857">
              Top Medical Destinations
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-858">
              We partner with elite facilities in the world's most sought-after
              medical tourism hubs.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-859">
            {[{
            slug: 'mexico',
            country: 'Mexico',
            specialties: 'Dental, Bariatric, Cosmetic',
            image: 'https://images.unsplash.com/photo-1518105779142-d975f22f1b0a?q=80&w=2940&auto=format&fit=crop'
          }, {
            slug: 'thailand',
            country: 'Thailand',
            specialties: 'Cosmetic, Orthopedic, Wellness',
            image: 'https://images.unsplash.com/photo-1552465011-b4e21bf6e79a?q=80&w=2839&auto=format&fit=crop'
          }, {
            slug: 'turkey',
            country: 'Turkey',
            specialties: 'Hair Transplant, Dental, Cosmetic',
            image: 'https://images.unsplash.com/photo-1524231757912-21f4fe3a7200?q=80&w=2938&auto=format&fit=crop'
          }, {
            slug: 'india',
            country: 'India',
            specialties: 'Cardiac, Orthopedic, Oncology',
            image: 'https://images.unsplash.com/photo-1524492412937-b28074a5d7da?q=80&w=2942&auto=format&fit=crop'
          }].map(dest => <Link to={`/destinations/${dest.slug}`} key={dest.country} className="group relative overflow-hidden rounded-2xl aspect-[4/5] cursor-pointer block" data-id="element-860">
                <img src={dest.image} alt={`${dest.country} landscape`} className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-861" />
                <div className="absolute inset-0 bg-slate-900/60" data-id="element-862" />
                <div className="absolute bottom-0 left-0 p-6" data-id="element-863">
                  <h3 className="text-2xl font-bold text-white mb-1" data-id="element-864">
                    {dest.country}
                  </h3>
                  <p className="text-slate-200 text-sm" data-id="element-865">{dest.specialties}</p>
                </div>
              </Link>)}
          </div>
        </section>

        {/* Global Medical Settlement Promo */}
        <section className="py-20 px-6 md:px-12 max-w-7xl mx-auto" data-id="element-866">
          <div className="bg-blue-600 rounded-3xl overflow-hidden shadow-2xl flex flex-col lg:flex-row" data-id="element-867">
            <div className="lg:w-1/2 p-10 md:p-16 flex flex-col justify-center" data-id="element-868">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-blue-500/30 text-blue-50 mb-6 w-fit" data-id="element-869">
                <Globe className="w-4 h-4" data-id="element-870" />
                <span className="text-sm font-semibold tracking-wide" data-id="element-871">
                  Long-Term Medical Settlement
                </span>
              </div>
              <h2 className="text-3xl md:text-4xl font-bold text-white mb-6 leading-tight" data-id="element-872">
                Discover Global Medical Tourism Cities for Settlement
              </h2>
              <p className="text-blue-100 text-lg mb-8 leading-relaxed" data-id="element-873">
                Looking for more than just a quick procedure? Explore our
                curated list of global cities perfectly suited for long-term
                medical stays, rehabilitation, and retirement. Enjoy world-class
                healthcare access, established expat communities, and a high
                quality of life at a fraction of the cost.
              </p>
              <div className="flex flex-wrap gap-4" data-id="element-874">
                <Button size="lg" className="bg-white text-blue-600 hover:bg-slate-100" onClick={() => navigate('/destinations')} data-id="element-875">
                  Explore Settlement Hubs
                </Button>
                <Button variant="outline" size="lg" className="bg-transparent text-white border-white hover:bg-blue-700 hover:text-white hover:border-white" onClick={scrollToContact} data-id="element-876">
                  Inquire About Relocation
                </Button>
              </div>
            </div>
            <div className="lg:w-1/2 relative min-h-[300px] lg:min-h-auto" data-id="element-877">
              <img src="https://images.unsplash.com/photo-1512453979798-5ea266f8880c?q=80&w=2940&auto=format&fit=crop" alt="Luxury medical settlement city" className="absolute inset-0 w-full h-full object-cover" data-id="element-878" />
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-transparent lg:w-1/3" data-id="element-879" />
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="bg-slate-900 text-white py-20" data-id="element-880">
          <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-881">
            <div className="text-center mb-16" data-id="element-882">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" data-id="element-883">
                Representative Patient Experiences
              </h2>
              <p className="text-lg text-slate-300 max-w-2xl mx-auto" data-id="element-884">
                Illustrative examples of how patients can transform their lives
                through coordinated care abroad.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8" data-id="element-885">
              {[{
              quote: 'I saved over $20,000 on my full mouth restoration in Costa Rica. The clinic was more modern than my dentist back home, and the care was exceptional.',
              name: 'M. T.',
              procedure: 'Dental Implants',
              destination: 'Costa Rica',
              savings: 'Illustrative savings: $22,000',
              avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=2787&auto=format&fit=crop'
            }, {
              quote: 'The care coordinator handled everything from my flights to my hotel. My knee replacement in Mexico went perfectly, and I enjoyed a beautiful recovery by the ocean.',
              name: 'S. L.',
              procedure: 'Knee Replacement',
              destination: 'Mexico',
              savings: 'Illustrative savings: $18,500',
              avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?q=80&w=2787&auto=format&fit=crop'
            }, {
              quote: "After years of struggling with IVF costs in the US, we went to Spain. The doctors were incredibly compassionate, and we're now expecting our first child!",
              name: 'E. & D. R.',
              procedure: 'IVF Treatment',
              destination: 'Spain',
              savings: 'Illustrative savings: $15,000',
              avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=2940&auto=format&fit=crop'
            }].map((testimonial, i) => <Card key={i} className="bg-slate-800 border-slate-700 text-white" data-id="element-886">
                  <CardContent className="pt-6" data-id="element-887">
                    <div className="flex gap-1 mb-4 text-yellow-400" data-id="element-888">
                      {[1, 2, 3, 4, 5].map(star => <svg key={star} className="w-5 h-5 fill-current" viewBox="0 0 20 20" data-id="element-889">
                          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" data-id="element-890" />
                        </svg>)}
                    </div>
                    <p className="text-slate-300 italic mb-6" data-id="element-891">
                      "{testimonial.quote}"
                    </p>
                    <div className="flex items-center gap-4" data-id="element-892">
                      <Avatar data-id="element-893">
                        <AvatarImage src={testimonial.avatar} alt={testimonial.name} data-id="element-894" />
                        <AvatarFallback data-id="element-895">{testimonial.name[0]}</AvatarFallback>
                      </Avatar>
                      <div data-id="element-896">
                        <p className="font-semibold" data-id="element-897">{testimonial.name}</p>
                        <p className="text-sm text-slate-400" data-id="element-898">
                          {testimonial.procedure} in {testimonial.destination}
                        </p>
                        <p className="text-xs text-slate-500 mt-1" data-id="element-899">
                          Illustrative example
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>)}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="py-20 px-6 md:px-12 max-w-3xl mx-auto" data-id="element-900">
          <div className="text-center mb-12" data-id="element-901">
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-902">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-slate-600" data-id="element-903">
              Everything you need to know about receiving medical care abroad.
            </p>
          </div>

          <Accordion type="single" collapsible className="w-full" data-id="element-904">
            <AccordionItem value="item-1" data-id="element-905">
              <AccordionTrigger data-id="element-906">
                Is it safe to get medical treatment abroad?
              </AccordionTrigger>
              <AccordionContent data-id="element-907">
                Yes, when carefully planned. We coordinate care exclusively with
                facilities that maintain recognized international
                accreditations. These institutions adhere to stringent clinical
                protocols and safety standards that are designed to mirror or
                exceed those found in major domestic healthcare systems.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-2" data-id="element-908">
              <AccordionTrigger data-id="element-909">
                How are the hospitals and doctors vetted?
              </AccordionTrigger>
              <AccordionContent data-id="element-910">
                Our vetting methodology is comprehensive. We evaluate facilities
                based on their adherence to international accreditation
                standards, such as those modeled by organizations like the Joint
                Commission International (cited here illustratively). We also
                review the credentials of the medical staff, prioritizing
                surgeons who hold board certifications or have completed
                fellowships in recognized medical hubs. Facility infrastructure,
                including intensive care capabilities, is also a critical factor
                in our assessment.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-3" data-id="element-911">
              <AccordionTrigger data-id="element-912">
                What happens if I need follow-up care when I return home?
              </AccordionTrigger>
              <AccordionContent data-id="element-913">
                Comprehensive care planning includes your return home. Before
                you leave the destination, you will receive all medical records
                translated into English. We can also help coordinate with your
                local primary care physician or specialist for any necessary
                follow-up care.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-4" data-id="element-914">
              <AccordionTrigger data-id="element-915">
                Does my health insurance cover medical tourism?
              </AccordionTrigger>
              <AccordionContent data-id="element-916">
                While traditional US health insurance typically does not cover
                elective procedures abroad, some self-funded employer plans are
                beginning to offer medical tourism benefits. However, the
                out-of-pocket cost abroad is often significantly lower than the
                deductible and co-pays for the same procedure in the US.
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="item-5" data-id="element-917">
              <AccordionTrigger data-id="element-918">
                Do I need to arrange my own travel?
              </AccordionTrigger>
              <AccordionContent data-id="element-919">
                You maintain the flexibility to manage your own travel logistics
                if preferred. However, our care coordination service includes
                comprehensive logistical support. We can assist with scheduling
                flights, arranging secure airport transfers, and booking
                appropriate accommodations near the medical facility for you and
                any traveling companions, ensuring a streamlined experience.
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </section>
      </main>

      <AppFooter data-id="element-920" />
    </div>;
}